import { Effect, EffectProperties, EffectFields } from '../../core/Effect';
import * as PostProcessing from '@react-three/postprocessing';
import { forwardRef } from 'react';
import * as THREE from 'three';
import { Field } from '@/types/core/Field';
import { BlendFunction } from 'postprocessing';

export interface LensFlareEffectProperties extends EffectProperties {
  starBurst: boolean;
  flareSize: number;
  flareSpeed: number;
  glareSize: number;
  lensPosition: {
    x: number;
    y: number;
    z: number;
  };
  smoothTime: number;
}

// Create a reusable LensFlare component that can be used in both preview and world
const LensFlareComponent = forwardRef<any, { properties: LensFlareEffectProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;

  if (isPreview) {
    return null;
  }
  
  const { starBurst, flareSize, flareSpeed, glareSize, lensPosition, smoothTime, blendFunction } = properties;

  // Convert blendFunction string to number if it exists
  const blendFunctionValue = blendFunction ? 
    BlendFunction[blendFunction as keyof typeof BlendFunction] : 
    BlendFunction.SCREEN;

  return (
    <PostProcessing.LensFlare
      lensPosition={new THREE.Vector3(lensPosition.x, lensPosition.y, lensPosition.z)}
      smoothTime={smoothTime}
      starBurst={starBurst}
      flareSize={flareSize}
      flareSpeed={flareSpeed}
      glareSize={glareSize}
      blendFunction={blendFunctionValue}
    />
  );
});

export const LensFlareEffectItem: Effect<LensFlareEffectProperties> = {
  id: '',
  name: 'Lens Flare',
  type: 'effect',
  subtype: 'LensFlare',
  icon: 'Effect',
  properties: {
    starBurst: true,
    flareSize: 0.45,
    flareSpeed: 0.75,
    glareSize: 0.55,
    lensPosition: {
      x: 0,
      y: 0,
      z: 0
    },
    smoothTime: 0.1,
    blendFunction: 'SCREEN',
  },
  fields: [
    ...EffectFields,
    {
      key: 'properties.starBurst',
      type: 'boolean',
      label: 'Star Burst',
      default: true,
      panel: 'effect',
    },
    {
      key: 'properties.flareSize',
      type: 'number',
      label: 'Flare Size',
      default: 0.45,
      min: 0,
      max: 1,
      step: 0.01,
      panel: 'effect',
    },
    {
      key: 'properties.flareSpeed',
      type: 'number',
      label: 'Flare Speed',
      default: 0.75,
      min: 0,
      max: 2,
      step: 0.01,
      panel: 'effect',
    },
    {
      key: 'properties.glareSize',
      type: 'number',
      label: 'Glare Size',
      default: 0.55,
      min: 0,
      max: 1,
      step: 0.01,
      panel: 'effect',
    },
    {
      key: 'properties.lensPosition.x',
      type: 'number',
      label: 'Lens Position X',
      default: 0,
      min: -10,
      max: 10,
      step: 0.1,
      panel: 'effect',
    },
    {
      key: 'properties.lensPosition.y',
      type: 'number',
      label: 'Lens Position Y',
      default: 0,
      min: -10,
      max: 10,
      step: 0.1,
      panel: 'effect',
    },
    {
      key: 'properties.lensPosition.z',
      type: 'number',
      label: 'Lens Position Z',
      default: 0,
      min: -10,
      max: 10,
      step: 0.1,
      panel: 'effect',
    },
    {
      key: 'properties.smoothTime',
      type: 'number',
      label: 'Smooth Time',
      default: 0.1,
      min: 0,
      max: 1,
      step: 0.01,
      panel: 'effect',
    },
    {
      key: 'properties.blendFunction',
      type: 'select',
      label: 'Blend Function',
      default: 'SCREEN',
      options: [
        { value: 'NORMAL', label: 'Normal' },
        { value: 'ADD', label: 'Add' },
        { value: 'SUBTRACT', label: 'Subtract' },
        { value: 'MULTIPLY', label: 'Multiply' },
        { value: 'SCREEN', label: 'Screen' },
        { value: 'OVERLAY', label: 'Overlay' },
      ],
      panel: 'effect',
    }
  ],
  render: (properties: LensFlareEffectProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <LensFlareComponent properties={properties} isPreview={isPreview} ref={ref as any} />;
  }
}; 